// Helpers
import { errorLogger } from "@/Common/helpers/logger";

// Fragments
import { ReconciliationFragment } from "../fragments/reconciliation";

// Types
import { apiInstance, gql } from "@/services/httpAdapter";
import { IPaginatedResponse } from "@/Common/api/types/pagination";
import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import {
	IReconciliation,
	IReconciliationReason,
	IReconciliationResolution,
	IReconciliationStatus,
} from "@DataPages/types/inventory/models/reconciliation";

export async function getReconciliationData(
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	pagination: { first: number; after: string }
) {
	const query = gql`
		query getReconciliation($pagination: PaginationInput!, $input: PerformReconciliationInput!) {
			getReconciliation(pagination: $pagination, input: $input) {
				edges {
					node {
						...Reconciliation
					}
					cursor
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
		${ReconciliationFragment}
	`;

	const variables = {
		pagination,
		input: {
			locationId,
			commissionCycleId,
			payPeriodId,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getReconciliation", IPaginatedResponse<IReconciliation>> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getReconciliation;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export const getReconciliationStatus = async (locationId: string, commissionCycleId: string, payPeriodId: string) => {
	try {
		const query = gql`
			query getReconciliationStatus($input: PerformReconciliationInput) {
				getReconciliationStatus(input: $input) {
					_id
					commissionCycleId
					locationId
					payPeriodId
					newVehicleReconciliation {
						status
						endDate
						startDate
						error
					}
					usedVehicleReconciliation {
						status
						endDate
						startDate
						error
					}
				}
			}
		`;

		const variables = {
			input: { locationId, commissionCycleId, payPeriodId },
		};

		const response: GraphqlQueryResponse<"getReconciliationStatus", IReconciliationStatus> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		return response.data.data?.getReconciliationStatus;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
};

export const getReconciliationReasons = async (locationId: string) => {
	try {
		const query = gql`
			query getReconciliationReasons($locationId: String!) {
				getReconciliationReasons(locationId: $locationId) {
					_id
					reason
				}
			}
		`;

		const variables = {
			locationId,
		};

		const response: GraphqlQueryResponse<"getReconciliationReasons", IReconciliationReason[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		return response.data.data?.getReconciliationReasons;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
};

export const getReconciliationResolutions = async (locationId: string) => {
	try {
		const query = gql`
			query getReconciliationResolutions($locationId: String!) {
				getReconciliationResolutions(locationId: $locationId) {
					_id
					resolution
				}
			}
		`;

		const variables = {
			locationId,
		};

		const response: GraphqlQueryResponse<"getReconciliationResolutions", IReconciliationResolution[]> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);

		return response.data.data?.getReconciliationResolutions;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
};
